import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { AuthForm } from './AuthForm'
import { UserProfileDashboard } from './UserProfileDashboard'
import { Button } from './ui/button'
import { motion } from 'framer-motion'
import { User, LogOut } from 'lucide-react'

export const ProfileDemo: React.FC = () => {
  const { user, loading, signOut } = useAuth()
  const [showAuthForm, setShowAuthForm] = useState(false)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    )
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-green-600">TourPin</h1>
              <span className="text-sm text-gray-500">Profile Demo</span>
            </div>

            <nav className="flex items-center space-x-4">
              {user ? (
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-700">
                    <User className="h-4 w-4" />
                    <span>{user.email}</span>
                  </div>
                  
                  <Button
                    onClick={handleSignOut}
                    variant="outline"
                    size="sm"
                    className="flex items-center space-x-2"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowAuthForm(true)}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  Sign In / Sign Up
                </Button>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8">
        
        {user ? (
          <UserProfileDashboard />
        ) : (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            
            {showAuthForm ? (
              <div className="max-w-md mx-auto">
                <AuthForm onClose={() => setShowAuthForm(false)} />
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-8"
              >
                <div className="space-y-4">
                  <h2 className="text-4xl font-bold text-gray-900">
                    Welcome to TourPin Profile Demo
                  </h2>
                  <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    Experience our comprehensive user profile management system with 
                    Supabase authentication, file uploads, and guide profiles.
                  </p>
                </div>

                <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
                  <h3 className="text-2xl font-semibold mb-6">Features Included:</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-green-600">Authentication</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Email/Password signup</li>
                        <li>• Google OAuth</li>
                        <li>• GitHub OAuth</li>
                        <li>• Session management</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-semibold text-green-600">Profile Management</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Profile creation/editing</li>
                        <li>• Avatar image upload</li>
                        <li>• Public profile view</li>
                        <li>• Form validation</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-semibold text-green-600">Guide Features</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Guide profile creation</li>
                        <li>• Specialties & languages</li>
                        <li>• Rating system</li>
                        <li>• Hourly rate setting</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-semibold text-green-600">Storage & Database</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Supabase integration</li>
                        <li>• File storage for avatars</li>
                        <li>• Real-time updates</li>
                        <li>• Type-safe database queries</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Button
                    onClick={() => setShowAuthForm(true)}
                    size="lg"
                    className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 text-lg"
                  >
                    Get Started
                  </Button>
                  
                  <p className="text-sm text-gray-500">
                    Sign up or sign in to explore the profile management features
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </main>
    </div>
  )
}

export default ProfileDemo